import { cloneDeep, uniq } from 'lodash';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import { parseWildcardDates } from '../helpers/dateHelpers';
import { reduceRight } from './OrderHelpers';
import { extractValueFromOrderTags } from './OrderTags';
import * as OrderLineMappingG5Helper from './OrderLineMappingHelper';
import * as uuid from 'uuid';
import { DiscountScope, } from '../plugins/commonTypes/ProductTypes';
import getLuxonImpl from '../plugins/commonLibraries/timezone/timeboxImpl';
import { ApmHelper, ApmSpanType } from '../libraries/ApmHelper';
import { isPriceRuleValid, isGlobalCartRule, shouldApplyEmployeeDiscount, shouldApplySunsetPointsDiscount, shouldRuleBeAppliedToShop, isFreeMealPriceRule, } from '../priceRules/priceRulesHelpers';
import { isMenuByProduct } from '../libraries/Utils';
import VatRatesMgrFactory from '../vatRates/VatRatesMgr';
import { createAdditionalLines } from '../orderlines/createAdditionalLines';
import { generateDiscountLine } from '../orderlines/generateDiscountLine';
const getIdAsName = (guid, prefix) => {
    return prefix + guid.substring(guid.length - 8).replace(/0/g, '');
};
const MY_GENERATION = DomainCoreGeneration.Generation5;
const createOrderLineFromGroup = (p, g) => {
    const group = p.getGroup(g.groupId);
    const orderLine = {
        name: group.name || group.options.description || getIdAsName(group.id, 'Group'),
        type: 'Aggregation',
        options: group.options ? cloneDeep(group.options) : {},
        subLines: [],
        generation: MY_GENERATION,
        _source: { entity: 'group', id: group.id },
    };
    return orderLine;
};
let discountScopes = [DiscountScope.All];
let isMenuProduct = false;
export const calculateVat = (vatRatePerMille, totalPrice) => {
    const vatRatesMgr = VatRatesMgrFactory();
    const vatRate = vatRatesMgr.getPricelistVatRatePerMille(vatRatePerMille || 0);
    return vatRatesMgr.calculateTotalVat(totalPrice, vatRate, true);
};
const createOrderLineFromProduct = (productCatalog, product, rootProductId, quantity, depth, productSelectionId, category = null, rcmSource = null, channel = null, deliveryMethod = null, customerType = null, cartProductsMap = null) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
    const computeOrderLinePrice = () => {
        var _a;
        let rootProduct;
        if (isMenuProduct) {
            rootProduct = productCatalog.getProduct(rootProductId, true);
        }
        if (product.priceStructure && product.priceStructure.priceInGroup && depth > 0) {
            if ((_a = rootProduct === null || rootProduct === void 0 ? void 0 : rootProduct.options) === null || _a === void 0 ? void 0 : _a.isMiniMenu) {
                return product.priceStructure.defaultPrice;
            }
            return product.priceStructure.priceInGroup;
        }
        else if (product.price) {
            return product.price;
        }
        return undefined;
    };
    const discountScope = ((_b = (_a = product === null || product === void 0 ? void 0 : product.discount) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.discountScope) || DiscountScope.All;
    if (depth === 0) {
        isMenuProduct = isMenuByProduct(product);
        discountScopes = isMenuProduct
            ? [DiscountScope.Group, DiscountScope.All]
            : [DiscountScope.Single, DiscountScope.All];
    }
    const orderLinePrice = computeOrderLinePrice();
    const orderLineQuantity = quantity;
    const computeTotalPrice = () => {
        if (orderLinePrice) {
            return orderLinePrice * (orderLineQuantity !== undefined ? orderLineQuantity : 1);
        }
        return 0;
    };
    const totalPrice = computeTotalPrice();
    const totalVat = calculateVat(product.vatRatePerMille, totalPrice);
    let discount = discountScopes.includes(discountScope) ? product.discount : null;
    if (channel &&
        ((_e = (_d = (_c = product === null || product === void 0 ? void 0 : product.discount) === null || _c === void 0 ? void 0 : _c.options) === null || _d === void 0 ? void 0 : _d.channels) === null || _e === void 0 ? void 0 : _e.length) &&
        !((_g = (_f = product === null || product === void 0 ? void 0 : product.discount) === null || _f === void 0 ? void 0 : _f.options) === null || _g === void 0 ? void 0 : _g.channels.includes(channel))) {
        discount = null;
    }
    if (Number.isInteger(deliveryMethod) &&
        ((_k = (_j = (_h = product === null || product === void 0 ? void 0 : product.discount) === null || _h === void 0 ? void 0 : _h.options) === null || _j === void 0 ? void 0 : _j.deliveryMethods) === null || _k === void 0 ? void 0 : _k.length) &&
        !((_m = (_l = product === null || product === void 0 ? void 0 : product.discount) === null || _l === void 0 ? void 0 : _l.options) === null || _m === void 0 ? void 0 : _m.deliveryMethods.includes(+deliveryMethod))) {
        discount = null;
    }
    if (customerType &&
        ((_q = (_p = (_o = product === null || product === void 0 ? void 0 : product.discount) === null || _o === void 0 ? void 0 : _o.options) === null || _p === void 0 ? void 0 : _p.customerTypes) === null || _q === void 0 ? void 0 : _q.length) &&
        !((_s = (_r = product === null || product === void 0 ? void 0 : product.discount) === null || _r === void 0 ? void 0 : _r.options) === null || _s === void 0 ? void 0 : _s.customerTypes.includes(customerType))) {
        discount = null;
    }
    if (((_v = (_u = (_t = product === null || product === void 0 ? void 0 : product.discount) === null || _t === void 0 ? void 0 : _t.options) === null || _u === void 0 ? void 0 : _u.qualifyingProductIds) === null || _v === void 0 ? void 0 : _v.length) &&
        !(cartProductsMap && product.discount.options.qualifyingProductIds.find((qp) => cartProductsMap === null || cartProductsMap === void 0 ? void 0 : cartProductsMap[qp]))) {
        discount = null;
    }
    const orderLine = Object.assign(Object.assign(Object.assign({ isMenu: isMenuProduct, category: category, type: 'Product', productId: product.id, name: product.name || getIdAsName(product.id, 'product'), subLines: [], quantity: orderLineQuantity, aggregatedQuantity: undefined, options: Object.assign(Object.assign({}, product.options), { foreignIds: product.foreignIds, foreignNames: product.foreignNames, overrides: product.overrides, priceStructure: product.priceStructure }) || undefined, unitType: product.unit || undefined, accounting: product.options.accounting || undefined, discount: discount, applicableDiscounts: product.applicableDiscounts, price: orderLinePrice, totalPrice, rootProductId: rootProductId, totalPriceAccum: undefined, generation: MY_GENERATION, productSelectionId: productSelectionId, vatRatePerMille: product.vatRatePerMille, totalVat }, (product.vatCategoryId ? { vatCategoryId: product.vatCategoryId } : {})), (product.vatCategoryName ? { vatCategoryName: product.vatCategoryName } : {})), { rcmSource: rcmSource });
    if (!orderLine.accounting)
        delete orderLine.accounting;
    if (!orderLine.discount)
        delete orderLine.discount;
    return orderLine;
};
const createOrderLineFromConfig = (config, configSelectionNode) => {
    const _options = config.options.options;
    const selectedValue = Array.isArray(configSelectionNode.selectedValue)
        ? configSelectionNode.selectedValue
        : configSelectionNode.selectedValue !== undefined
            ? [configSelectionNode.selectedValue]
            : [];
    const orderLine = {
        name: config.name || getIdAsName(config.id, 'config'),
        type: 'Config',
        options: config.options,
        selectedOptions: _options
            .filter((opt, index) => selectedValue.indexOf(index) !== -1)
            .map((op) => new Object(Object.assign({}, op))),
        unselectedOptions: _options.reduce((acc, opt, index) => {
            if (selectedValue.indexOf(index) === -1) {
                acc.push(new Object(Object.assign(Object.assign({}, opt), { optionIndex: index, configId: config.id })));
            }
            return acc;
        }, []),
        subLines: [],
        generation: MY_GENERATION,
    };
    return orderLine;
};
const mapIncludedInMenuPrice = (productCatalog, rootProductId, product) => {
    const { groups } = product;
    if (groups && groups.length > 0) {
        const updatedGroups = groups.reduce((dict, group) => {
            const defaultSelectedProductId = group.options.defaultSelected;
            const defaultSelectedProduct = group.products.find((p) => p.id === defaultSelectedProductId) || group.products[0];
            const rootProduct = productCatalog.getProduct(rootProductId, true);
            const includedInMenuPrice = defaultSelectedProduct.priceStructure &&
                defaultSelectedProduct.priceStructure.priceInGroup &&
                !rootProduct.options.isMiniMenu
                ? defaultSelectedProduct.priceStructure.priceInGroup
                : defaultSelectedProduct.price;
            dict[group.id] = includedInMenuPrice;
            return dict;
        }, {});
        return updatedGroups;
    }
    return {};
};
const recurse = (productCatalog, orderLineArray, productSelectionNode, depth, rootProductId, options = {}) => {
    const product = productCatalog.getProduct(productSelectionNode.productId, true);
    if (product.options.isNoThanks) {
        return;
    }
    const includedInMenuPrices = mapIncludedInMenuPrice(productCatalog, rootProductId, product);
    const effectiveQuantity = productSelectionNode.quantity;
    const orderLine = createOrderLineFromProduct(productCatalog, product, rootProductId, effectiveQuantity, depth, productSelectionNode.productSelectionId, productSelectionNode.category, productSelectionNode.rcmSource, options.channel, options.deliveryMethod, options.customerType, options.cartProductsMap);
    if (orderLine.type === 'Product' && productSelectionNode.quantity === undefined) {
        orderLine.quantity = 1;
    }
    orderLine.subLines = [];
    if (productSelectionNode.configurations) {
        productSelectionNode.configurations.forEach((configSelectionNode) => {
            const config = product.getConfig(configSelectionNode.configurationId);
            const configLine = createOrderLineFromConfig(config, configSelectionNode);
            if (orderLine.type === 'Product') {
                orderLine.subLines.push(configLine);
            }
        });
    }
    if (productSelectionNode.groups) {
        productSelectionNode.groups.forEach((g) => {
            var _a, _b;
            const groupLine = createOrderLineFromGroup(product, g);
            if (groupLine.options.includedInMenuPrice && includedInMenuPrices[(_a = groupLine._source) === null || _a === void 0 ? void 0 : _a.id]) {
                groupLine.options.includedInMenuPrice = includedInMenuPrices[(_b = groupLine._source) === null || _b === void 0 ? void 0 : _b.id];
            }
            groupLine.subLines = [];
            g.selectedProducts.forEach((p) => {
                const productSelectionNodeInner = typeof p === 'string'
                    ? { productId: p }
                    : p;
                recurse(productCatalog, groupLine.subLines, productSelectionNodeInner, depth + 1, rootProductId, options);
            });
            groupLine.generation = MY_GENERATION;
            if (groupLine.options.defaultSelected) {
                const product = productCatalog.getProduct(groupLine.options.defaultSelected, true);
                if (product) {
                    groupLine.options.defaultSelectedData = {
                        priceStructure: product.priceStructure,
                    };
                }
            }
            orderLine.subLines.push(groupLine);
        });
    }
    orderLine.generation = MY_GENERATION;
    orderLineArray.push(orderLine);
};
const helperFunctions = {
    getIdAsName,
    recurse,
    createOrderLineFromGroup,
    createOrderLineFromProduct,
    createOrderLineFromConfig,
};
const mapToOrderLinesG5 = (productCatalog, productSelection, options = {}, orderTags = [], includedPriceDisabled = false, discountTypeDisabled = []) => {
    if (!productCatalog || !productCatalog.products || !productCatalog.products.length) {
        return [];
    }
    const filterBasedOnVoucher = (rule) => OrderLineMappingG5Helper.shouldApplyDiscount(rule, options);
    const { priceRules, selectedShopId, currentDate } = productCatalog;
    const filteredByShop = priceRules
        .filter((rule) => {
        if (selectedShopId === null)
            return true;
        return shouldRuleBeAppliedToShop(rule.shopPattern, selectedShopId);
    })
        .filter(filterBasedOnVoucher)
        .filter((rule) => {
        if (isFreeMealPriceRule(rule)) {
            return extractValueFromOrderTags('FirstMeal', orderTags) === 'true';
        }
        return true;
    })
        .filter((pr) => {
        var _a, _b, _c, _d, _e;
        if (!pr.options) {
            return true;
        }
        const channel = options === null || options === void 0 ? void 0 : options.channel;
        const isPriceRuleAvailableForChannel = [undefined, null].includes(channel) || !((_a = pr.options.channels) === null || _a === void 0 ? void 0 : _a.length) || pr.options.channels.includes(channel);
        const customerType = options === null || options === void 0 ? void 0 : options.customerType;
        const isPriceRuleAvailableForCustomerType = [undefined, null].includes(customerType) ||
            !((_b = pr.options.customerTypes) === null || _b === void 0 ? void 0 : _b.length) ||
            pr.options.customerTypes.includes(customerType);
        const deliveryMethod = options === null || options === void 0 ? void 0 : options.deliveryMethod;
        const isPriceRuleAvailableForDeliveryMethod = !Number.isInteger(deliveryMethod) ||
            !((_c = pr.options.deliveryMethods) === null || _c === void 0 ? void 0 : _c.length) ||
            pr.options.deliveryMethods.includes(deliveryMethod);
        const isPriceRuleAvailableByQualifyingProducts = !((_e = (_d = pr === null || pr === void 0 ? void 0 : pr.options) === null || _d === void 0 ? void 0 : _d.qualifyingProductIds) === null || _e === void 0 ? void 0 : _e.length) ||
            ((options === null || options === void 0 ? void 0 : options.cartProductsMap) &&
                pr.options.qualifyingProductIds.find((qp) => { var _a; return (_a = options === null || options === void 0 ? void 0 : options.cartProductsMap) === null || _a === void 0 ? void 0 : _a[qp]; }));
        return (isPriceRuleAvailableForChannel &&
            isPriceRuleAvailableForDeliveryMethod &&
            isPriceRuleAvailableForCustomerType &&
            isPriceRuleAvailableByQualifyingProducts);
    });
    const filteredRules = filteredByShop.filter((rule) => {
        const { Formats: TimeBoxFormats, timebox: TimeBox } = getLuxonImpl();
        if (Array.isArray(rule.orderingDatePattern)) {
            const shopCurrentDate = TimeBox.convertRelativeStringDate(currentDate, productCatalog.userTimeZone, productCatalog.pickupTimeZone)
                .format(TimeBoxFormats.YYYY_MM_DD)
                .exec();
            const limitDates = uniq(rule.orderingDatePattern.reduce((carry, dateFilterItem) => {
                return [...carry, ...parseWildcardDates(dateFilterItem, new Date(currentDate))];
            }, []));
            return limitDates.indexOf(shopCurrentDate) !== -1;
        }
        else if (Array.isArray(rule.weekDay)) {
            const weekDay = TimeBox.realNow().weekday().exec();
            return rule.weekDay.indexOf(weekDay) !== -1;
        }
        else {
            return false;
        }
    });
    const { global: globalRules } = filteredRules.reduce((acc, curr) => {
        if (curr.productSpecific === false) {
            const c = curr;
            return Object.assign(Object.assign({}, acc), { global: [...acc.global, c] });
        }
        const c = curr;
        return Object.assign(Object.assign({}, acc), { specific: [...acc.specific, c] });
    }, { specific: [], global: [] });
    const matchGlobalDiscount = (rule) => {
        if (shouldApplySunsetPointsDiscount(rule, options)) {
            return true;
        }
        if (shouldApplyEmployeeDiscount(rule, options)) {
            return true;
        }
        if (isFreeMealPriceRule(rule) && !rule.productSpecific) {
            return true;
        }
        if (isGlobalCartRule(rule)) {
            return true;
        }
        const membership = extractValueFromOrderTags('Membership', orderTags);
        const subscriptionCreatedDate = new Date(extractValueFromOrderTags('SubscriptionCreatedDate', orderTags));
        const switchedDateTag = extractValueFromOrderTags('SubscriptionSwitchedDate', orderTags);
        const subscriptionSwitchedDate = ['null', 'undefined', ''].includes(switchedDateTag)
            ? undefined
            : new Date(switchedDateTag);
        return isPriceRuleValid(membership, rule, subscriptionCreatedDate, subscriptionSwitchedDate);
    };
    const computedGlobalPriceRules = globalRules
        .filter(matchGlobalDiscount)
        .map((rule) => {
        const label = rule.options.label;
        if (label[label.length - 3] === '_') {
            return Object.assign(Object.assign({}, rule), { options: Object.assign(Object.assign({}, rule.options), { label: rule.options.label.slice(0, -3) }) });
        }
        return rule;
    })
        .filter(filterBasedOnVoucher)
        .sort((r1, r2) => r2.options.priority - r1.options.priority);
    const orderLines = [];
    productSelection.forEach((node) => helperFunctions.recurse(productCatalog, orderLines, node, 0, node.productId, options));
    reduceRight(orderLines, (acc, curr) => {
        curr.virtualId = uuid.v4();
        return null;
    }, null);
    function getIncludedPrice(curr) {
        const includedPrice = curr.options &&
            curr.options.foreignIds &&
            curr.options.foreignIds.onlinePosRules &&
            curr.options.foreignIds.onlinePosRules.cart &&
            curr.options.foreignIds.onlinePosRules.cart.find((c) => c.inclPrice);
        return includedPrice;
    }
    reduceRight(orderLines, (acc, curr) => {
        const includedPrice = includedPriceDisabled ? false : getIncludedPrice(curr);
        if (includedPrice && curr.type === 'Product') {
            const myQuantity = 1;
            const totalPrice = myQuantity * includedPrice.inclPrice;
            const totalVat = calculateVat(curr.vatRatePerMille, totalPrice);
            curr.subLines.push(Object.assign(Object.assign({ quantity: myQuantity, price: includedPrice.inclPrice, totalPrice, subLines: [], accounting: undefined, aggregatedQuantity: undefined, discount: undefined, applicableDiscounts: undefined, generation: '', options: undefined, productId: undefined, productSelectionId: undefined, totalPriceAccum: 0, type: 'Product', unitType: undefined, name: includedPrice.name, vatRatePerMille: curr.vatRatePerMille, totalVat }, (curr.vatCategoryId ? { vatCategoryId: curr.vatCategoryId } : {})), (curr.vatCategoryName ? { vatCategoryName: curr.vatCategoryName } : {})));
        }
        return acc;
    }, {});
    const rulesReducer = (sentOrderLines) => sentOrderLines.reduce((acc, ol) => {
        if (ol.type === 'Product') {
            if (ol.discount &&
                ol.discount.discountType === 'familyDiscount' &&
                !discountTypeDisabled.includes(ol.discount.discountType)) {
                if (!globalRules[0] || ol.discount.options.priority >= globalRules[0].options.priority) {
                    const combinatorDiscounts = ol.discount;
                    if (OrderLineMappingG5Helper.shouldApplyDiscount(combinatorDiscounts, options)) {
                        return Object.assign(Object.assign({}, acc), { [combinatorDiscounts.options.label]: combinatorDiscounts });
                    }
                }
            }
        }
        if (ol.subLines.length > 0) {
            return Object.assign(Object.assign({}, acc), rulesReducer(ol.subLines));
        }
        return acc;
    }, {});
    const availableRules = rulesReducer(orderLines);
    const combinatorRulesToApply = Object.values(availableRules).sort((rule1, rule2) => rule2.options.priority - rule1.options.priority);
    const shouldApplyGlobalRuleOnly = computedGlobalPriceRules[0] &&
        computedGlobalPriceRules[0].options.priority >=
            (combinatorRulesToApply[0] ? combinatorRulesToApply[0].options.priority : 0);
    const additionalLines = createAdditionalLines(orderTags, options);
    const applyAdditionalLines = (currentOrderLine, additionalLines) => {
        return additionalLines.reduce((acc, curr) => {
            return acc.concat([curr]);
        }, [...currentOrderLine]);
    };
    const generateOrderLineWithDiscount = (fullOrderLine, discountLineOrderLine) => {
        OrderLineMappingG5Helper.fixPricesForProductsWithFreeAdditionsG5(fullOrderLine);
        const [freeDiscountLine, discountLine] = generateDiscountLine(discountLineOrderLine, orderTags, productCatalog, computedGlobalPriceRules, options);
        const orderLinesWithDiscount = applyAdditionalLines(fullOrderLine, additionalLines)
            .concat(freeDiscountLine.type === 'FreeMeal' && freeDiscountLine.totalPrice !== 0 ? freeDiscountLine : null, discountLine.type === 'Discount' && (discountLine.totalPrice !== 0 || discountLine.options.hasReachedDailyLimit)
            ? discountLine
            : null)
            .filter(Boolean);
        OrderLineMappingG5Helper.accumulatePrices(orderLinesWithDiscount);
        OrderLineMappingG5Helper.aggregateQuantityTopDown(orderLinesWithDiscount);
        return orderLinesWithDiscount;
    };
    const applyCombinatorLines = (availableRules, orderLines, combinatorRulesToApply, options, orderTags) => {
        const [combinatorLines, unapliedOrderLines] = Object.keys(availableRules).length === 0
            ? [[], orderLines]
            : OrderLineMappingG5Helper.applyCombinatorDiscounts(orderLines, combinatorRulesToApply);
        const orderLinesEffectivePricesCombinator = OrderLineMappingG5Helper.applyDiscountsForOrderLines(combinatorLines, options, orderTags, computedGlobalPriceRules);
        const orderLinesEffectivePricesUnapplied = OrderLineMappingG5Helper.applyDiscountsForOrderLines(unapliedOrderLines, options, orderTags, computedGlobalPriceRules);
        const topLevelOL = [
            ...orderLinesEffectivePricesCombinator,
            ...orderLinesEffectivePricesUnapplied,
        ];
        return generateOrderLineWithDiscount(topLevelOL, orderLinesEffectivePricesUnapplied);
    };
    if (shouldApplyGlobalRuleOnly) {
        const orderLinesEffectivePrices = OrderLineMappingG5Helper.applyDiscountsForOrderLines(orderLines, options, orderTags, computedGlobalPriceRules);
        const orderLineWithDiscounts = generateOrderLineWithDiscount(orderLinesEffectivePrices, orderLinesEffectivePrices);
        const discountLine = orderLineWithDiscounts.find((ol) => ol.type === 'Discount');
        if (discountLine && discountLine.options.hasReachedDailyLimit) {
            return applyCombinatorLines(availableRules, orderLines, combinatorRulesToApply, options, orderTags);
        }
        return orderLineWithDiscounts;
    }
    else {
        return applyCombinatorLines(availableRules, orderLines, combinatorRulesToApply, options, orderTags);
    }
};
export default ApmHelper.apmDecorator(mapToOrderLinesG5, {
    apmLabel: 'OrderLineMappingsG5',
    apmType: ApmSpanType.PURE_FUNCTION,
});
